import { useThree, useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { Vector3, Quaternion, type Group } from 'three';
import { useShallow } from 'zustand/shallow';

import { EdgeDimension } from 'app/modules/wallforming/components/scenes/objects/dimensions/EdgeDimension';
import { FaceDimension } from 'app/modules/wallforming/components/scenes/objects/dimensions/FaceDimension';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EPSILON } from 'shared/constants';

interface PlanProjectionGroupProps {
    dir: Vector3;
    mid: Vector3;
    rotationReferenceDir?: Vector3;
    rotationDir?: Vector3;
    selectedPoiIndex: number;
}

export const PlanProjectionGroup = ({
    dir,
    mid,
    rotationReferenceDir,
    rotationDir = dir,
    selectedPoiIndex
}: PlanProjectionGroupProps) => {
    const workingPlane = usePlacementStore(useShallow((state) => state.workingPlane));

    const camera = useThree((state) => state.camera);
    const groupRef = useRef<Group | null>(null);
    const tempQuat = useRef<Quaternion>(new Quaternion());
    const forward = useRef<Vector3>(new Vector3(1, 0, 0));

    const selectedPoiType = usePlacementStore(useShallow((state) => state.pois[selectedPoiIndex]?.filterType));

    const projectedForward = useMemo(() => new Vector3(), []);
    const cross = useMemo(() => new Vector3(), []);
    const cameraDir = useMemo(() => new Vector3(), []);
    const projected = useMemo(() => new Vector3(), []);

    const piQuat = useMemo(() => {
        return rotationDir !== null
            ? new Quaternion().setFromAxisAngle(rotationDir.clone().normalize(), Math.PI)
            : null;
    }, [rotationDir]);

    useFrame(() => {
        if (!groupRef.current || !rotationDir || !piQuat || !workingPlane) {
            return;
        }

        camera.getWorldDirection(cameraDir).normalize().negate();
        projected.copy(cameraDir);

        if (rotationReferenceDir) {
            forward.current.copy(rotationReferenceDir);
        } else {
            forward.current.set(1, 0, 0);
            if (Math.abs(forward.current.dot(rotationDir)) > 0.99) {
                forward.current.set(0, 1, 0);
            }
        }

        projectedForward.copy(forward.current).projectOnPlane(rotationDir).normalize();

        projected.projectOnPlane(rotationDir);
        if (projected.lengthSq() < EPSILON) {
            return;
        }
        projected.normalize();

        const angle = projectedForward.angleTo(projected);
        cross.copy(projectedForward).cross(projected);
        const sign = cross.dot(rotationDir) < 0 ? -1 : 1;

        tempQuat.current.setFromAxisAngle(rotationDir, angle * sign);
        if (projectedForward.dot(workingPlane?.normal) < 0) {
            tempQuat.current.multiply(piQuat);
        }

        groupRef.current.setRotationFromQuaternion(tempQuat.current);
    });

    return (
        <group ref={groupRef} position={mid}>
            {selectedPoiType === POIFilterType.Wall ? (
                <FaceDimension dir={dir} mid={mid} selectedPoiIndex={selectedPoiIndex} />
            ) : (
                <EdgeDimension dir={dir} mid={mid} selectedPoiIndex={selectedPoiIndex} />
            )}
        </group>
    );
};
